import { ActionIcon, Box, Button, Center, Group, Modal, Stack, Text } from '@mantine/core';
import { IconChevronLeft, IconChevronRight } from '@tabler/icons-react';
import { ReactElement, useEffect, useMemo, useState } from 'react';
import { Communication, Attachment, Patient } from '@medplum/fhirtypes';
import { alert } from '@/services/alert';
import PatientCardSelector from '../../components/PatientCardSelector';

const FaxContentTitle = () => (
  <Text fw={500} size="lg">
    Preview
  </Text>
);

interface FaxContentProps {
  opened: boolean;
  onClose: (patient?: Patient) => Promise<void>;
  fax: Communication | null;
}

export function FaxContent({ opened, onClose, fax }: FaxContentProps): ReactElement {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [selectedPatient, setSelectedPatient] = useState<Patient | undefined>(undefined);

  const attachments = useMemo(() => {
    if (!fax?.payload) return [];
    return fax.payload
      .map((payload) => payload.contentAttachment)
      .filter((attachment): attachment is Attachment => Boolean(attachment?.url));
  }, [fax]);

  const currentAttachment = attachments[currentIndex];
  const isPdf = currentAttachment?.contentType === 'application/pdf';
  const isImage = currentAttachment?.contentType?.startsWith('image/');
  const hasMultipleAttachments = attachments.length > 1;

  useEffect(() => {
    if (opened && attachments.length > 0) {
      setCurrentIndex(0);
    }
    if (opened) {
      setSelectedPatient(fax?.subject as Patient | undefined);
    }
  }, [opened, attachments.length, fax?.id, fax?.subject]);

  useEffect(() => {
    // Should never happen since efax only supports PDF and images
    if (currentAttachment && !isPdf && !isImage) {
      alert.error('Unexpected file type: ' + currentAttachment?.contentType);
    }
  }, [currentAttachment, isPdf, isImage]);

  useEffect(() => {
    if (fax?.subject) {
      setSelectedPatient(fax.subject as Patient);
    }
    return () => setSelectedPatient(undefined);
  }, [fax]);

  const handlePrevious = () => {
    if (currentIndex > 0) {
      setCurrentIndex(currentIndex - 1);
    }
  };

  const handleNext = () => {
    if (currentIndex < attachments.length - 1) {
      setCurrentIndex(currentIndex + 1);
    }
  };

  const handleClose = () => {
    setCurrentIndex(0);
    onClose(selectedPatient);
  };

  if (!opened || !fax || attachments.length === 0) {
    return <></>;
  }

  return (
    <Modal
      opened={opened}
      onClose={handleClose}
      size="xl"
      centered
      withCloseButton
      title={<FaxContentTitle />}
      p={isPdf ? 0 : undefined}
    >
      <Box display="flex" style={{ alignItems: 'center' }} mb="md">
        <Text size="sm" fw={600} mr="xs">
          Linked to
        </Text>
        <Stack w="50%">
          <PatientCardSelector patient={selectedPatient} setSelectedPatient={setSelectedPatient} />
        </Stack>
      </Box>
      <Box>
        {isImage && (
          <Center h="100%" style={{ minHeight: '70vh', maxHeight: '70vh' }}>
            <img
              src={currentAttachment.url}
              alt={currentAttachment.title || `Fax attachment ${currentIndex + 1}`}
              style={{ maxWidth: '100%', maxHeight: '70vh', objectFit: 'contain' }}
            />
          </Center>
        )}

        {isPdf && (
          <Center h="70vh">
            <Box style={{ width: '80%', height: '100%' }}>
              <iframe
                key={currentAttachment.url}
                src={currentAttachment.url}
                style={{ width: '100%', height: '100%', border: 'none' }}
                title={currentAttachment.title || `PDF ${currentIndex + 1}`}
              />
            </Box>
          </Center>
        )}

        {!isPdf && !isImage && (
          <Center mt="md" mb="md">
            <Text>{currentAttachment.title}</Text>
            <Text>Content Type: {currentAttachment.contentType} not supported</Text>
          </Center>
        )}

        {hasMultipleAttachments && (
          <Center mt="md" mb="md">
            <Group gap="xs" align="center">
              <ActionIcon
                variant="filled"
                size="lg"
                onClick={handlePrevious}
                disabled={currentIndex === 0}
                color="#25639A"
              >
                <IconChevronLeft size={20} />
              </ActionIcon>
              {attachments.map((_, index) => (
                <Button
                  key={index}
                  variant={currentIndex === index ? 'filled' : 'light'}
                  size="sm"
                  onClick={() => setCurrentIndex(index)}
                  color={currentIndex === index ? '#25639A' : 'gray'}
                  style={{ minWidth: '36px' }}
                >
                  {index + 1}
                </Button>
              ))}
              <ActionIcon
                variant="filled"
                size="lg"
                onClick={handleNext}
                disabled={currentIndex === attachments.length - 1}
                color="#25639A"
              >
                <IconChevronRight size={20} />
              </ActionIcon>
            </Group>
          </Center>
        )}
      </Box>
    </Modal>
  );
}
